import { useEffect, useMemo } from 'react';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useParams, useSearchParams } from 'react-router';
import { createTheme, useTheme } from '@mui/material';

import { TEMPLATE_INTENT_WORKFLOW_ID_PREFIX } from '../workflow-builder-edit/constants';
import {
  darkModeBlue,
  darkModeGreen,
  darkModeGrey,
  darkModePurple,
} from './constants';
import isEqual from 'lodash/fp/isEqual';
import partition from 'lodash/fp/partition';
import {
  useGetIntentsQueryWithProduct,
  useIsFeatureFlagEnabled,
} from 'src/hooks/hooks';
import { useGetContextVariables } from 'src/hooks/useGetContextVariables';
import i18n from 'src/locales';
import { useGetHandoffConfigurationsQuery } from 'src/services/workflow-builder-metrics';
import {
  IntentFilter,
  IntentFiltersForEmail,
  IntentFiltersForIFrame,
  IntentFiltersMode,
} from 'src/slices/workflow-preview/types';
import {
  addPreviewLogs,
  clearPreviewLogs,
  selectPreviewIntentFilters,
  selectPreviewLogs,
  setPreviewConversationId,
} from 'src/slices/workflow-preview/workflowPreviewSlice';
import { useAppDispatch } from 'src/store/hooks';
import {
  ForethoughtWidgetPreviewLogsEvent,
  ForethoughtWidgetPreviewRestartEvent,
  isForethoughtWidgetPreviewLogsEvent,
  isForethoughtWidgetPreviewRestartEvent,
} from 'src/types/solveWidgetEventTypes';
import {
  ContextVariableValue,
  ConversationChannel,
  Language,
  TranslationChannel,
} from 'src/types/workflowBuilderAPITypes';
import { CHANNEL_TO_PRODUCT_MAP } from 'src/utils/constants';
import { CommonIntentWorkflowType } from 'src/utils/enums';
import { isEffectivelyAutoflow } from 'src/utils/solve/intentUtils';
import { isIntentActiveForChannel } from 'src/utils/solve/intentUtils';

export const getEnglishOption = (t: TFunction<'workflow-builder-preview'>) => ({
  label: t('utils.englishOption'),
  value: 'en',
});
export const getAllTagsOption = (t: TFunction<'workflow-builder-preview'>) => ({
  label: t('utils.allWorkflowTagsOption'),
  value: '',
});

export const confidenceScoreToWord = (confidenceScore: number) => {
  if (confidenceScore > 0.8) {
    return i18n.t('workflow-builder-preview:utils.confidenceScore.high');
  } else if (confidenceScore > 0.5) {
    return i18n.t('workflow-builder-preview:utils.confidenceScore.medium');
  } else {
    return i18n.t('workflow-builder-preview:utils.confidenceScore.low');
  }
};

export const getIframeUrl = ({
  initialIntentId,
  initialIntentPreviewConfig,
  isTemplate,
  language,
  previewToken,
  product,
  tag,
  templateIntentId,
  workflowCvs,
}: {
  isTemplate: boolean;
  initialIntentId?: string;
  initialIntentPreviewConfig?: {
    intentFilters: IntentFiltersForIFrame;
    isLiveMode: boolean;
    executeTicketCreation: boolean;
  };
  language: string;
  previewToken: string;
  product?: string | null;
  tag: string | null;
  templateIntentId?: string;
  workflowCvs: { name: string; value: ContextVariableValue }[];
}) => {
  const url = SOLVE_URL.replace('embed.js', '');
  const attrPrefix = isTemplate ? 'data-template-ft-' : 'data-ft-';

  const iframeURL = new URL(
    `${url}?data-api-key=${previewToken}&full-screen=true&config-ft-disable-close=true`,
  );
  iframeURL.searchParams.append('preview', 'true');

  iframeURL.searchParams.append('data-ft-conversation-language', language);
  if (tag) {
    iframeURL.searchParams.append('data-ft-workflow-tag', tag);
  }

  if (initialIntentId && initialIntentPreviewConfig) {
    iframeURL.searchParams.set('initial-intent-id', initialIntentId);
    // Keep potentially large filter snapshots out of the HTTP request URL.
    iframeURL.hash = new URLSearchParams({
      'preview-initial-intent-config': JSON.stringify(
        initialIntentPreviewConfig,
      ),
    }).toString();
  }

  if (templateIntentId) {
    iframeURL.searchParams.append('data-intent-id', templateIntentId);
  }

  if (product) {
    iframeURL.searchParams.append('data-solve-widget-product', product);
  }

  workflowCvs.forEach(cv => {
    const attrName = attrPrefix + cv.name.replaceAll(' ', '-');
    iframeURL.searchParams.append(attrName, String(cv.value));
  });

  return iframeURL.href;
};

export const getInteractiveEmailPreviewIframeUrl = ({
  conversationId,
  initialIntentId,
  language,
  previewToken,
  tag,
  token,
  workflowCvs,
}: {
  conversationId: string;
  initialIntentId: string;
  language: string;
  previewToken: string;
  tag: string | null;
  token: string | null;
  workflowCvs: { name: string; value: ContextVariableValue }[];
}) => {
  const url = getIframeUrl({
    isTemplate: false,
    language,
    previewToken,
    tag,
    workflowCvs,
  });

  const iframeURL = new URL(url);

  iframeURL.searchParams.append('initial-intent-id', initialIntentId);
  iframeURL.searchParams.append('data-interactive-email-preview', 'true');

  // TODO: replace hardcoded value once BE updates are merged
  iframeURL.searchParams.append(
    'data-interactive-email-conversation-id',
    conversationId,
  );
  if (token) {
    iframeURL.searchParams.append('data-interactive-email-token', token);
  }

  return iframeURL.href;
};

export const getLanguageOptions = (
  languages: Language[],
  languageCodesEnabled: string[],
  t: TFunction<'workflow-builder-preview'>,
) => {
  const filteredLanguageOptions = languageCodesEnabled.filter(langCode =>
    languages.find(lang => lang.code === langCode),
  );
  const languageOptions = filteredLanguageOptions.map(langCode => {
    const lang = languages.find(lang => lang.code === langCode);
    return {
      label: lang?.display_name ?? '',
      value: lang?.code ?? '',
    };
  });

  languageOptions.unshift(getEnglishOption(t));
  return languageOptions;
};

export const getWorkflowTagOptions = (
  workflowTags: string[],
  t: TFunction<'workflow-builder-preview'>,
) => {
  const workflowTagOptions = workflowTags.map(tag => ({
    label: tag,
    value: tag,
  }));
  workflowTagOptions.unshift(getAllTagsOption(t));

  return workflowTagOptions;
};

export const useGetPreviewLogs = () => {
  const dispatch = useAppDispatch();
  const previewLogs = useSelector(selectPreviewLogs);

  useEffect(() => {
    const handleMessage = (
      e: MessageEvent<
        ForethoughtWidgetPreviewLogsEvent | ForethoughtWidgetPreviewRestartEvent
      >,
    ) => {
      if (isForethoughtWidgetPreviewLogsEvent(e)) {
        const { conversationId, previewLogs: newLogs } = e.data;

        if (newLogs) {
          dispatch(addPreviewLogs(newLogs));
        }

        if (conversationId) {
          dispatch(setPreviewConversationId(conversationId));
        }
      } else if (isForethoughtWidgetPreviewRestartEvent(e)) {
        dispatch(clearPreviewLogs());
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch]);

  return previewLogs;
};

/**
 * Maps a context-variable-id-keyed dict to one keyed by display name.
 *
 * Unresolvable ids keep the raw id as their key rather than being dropped. An
 * api step whose output writes a deleted (or otherwise unlisted) CV used to
 * disappear from its preview log entirely -- no Output line, no value -- which
 * reads as "the step did nothing" when the step in fact ran and wrote
 * somewhere the console could not name.
 */
export const useGetCvNameDict = (cvIdDict: Record<string, unknown>) => {
  const { contextVariables } = useGetContextVariables();

  const cvIdKeys = Object.keys(cvIdDict);
  const cvNameDict: Record<string, unknown> = {};
  cvIdKeys.forEach(cvId => {
    const matchingCv = contextVariables.find(
      cv => cv.context_variable_id === cvId,
    );
    cvNameDict[matchingCv?.context_variable_name ?? cvId] = cvIdDict[cvId];
  });

  return cvNameDict;
};

export const useGetDefaultIntentFilters = (): {
  defaultIntentFilters: IntentFilter[];
  defaultIntentFiltersMapping: Record<string, IntentFilter>;
  isLoading: boolean;
} => {
  const channel = useChannel();
  const productOverride =
    channel && channel !== 'email'
      ? CHANNEL_TO_PRODUCT_MAP[channel as ConversationChannel]
      : '';
  const { data: intentsResponse, isLoading: isGetIntentsLoading } =
    useGetIntentsQueryWithProduct(undefined, {
      productOverride: productOverride || 'workflow_builder',
    });
  const {
    data: handoffConfigurationsResponse,
    isLoading: isHandoffConfigurationLoading,
  } = useGetHandoffConfigurationsQuery() ?? [];
  const isClassicWorkflowsDeprecated = useIsFeatureFlagEnabled(
    'classic_workflows_deprecated',
  );

  const intentFilters = useMemo(() => {
    const handoffConfigurations =
      handoffConfigurationsResponse?.configurations ?? [];

    // filter out handoffs
    const filteredIntents =
      intentsResponse?.intents.filter(intent => {
        const intentId = intent.intent_definition_id;
        return !handoffConfigurations.find(
          handoffConfig => handoffConfig.intent_definition_id === intentId,
        );
      }) ?? [];

    const intentFilters = filteredIntents.map(intent => {
      const isActive = isIntentActiveForChannel(
        intent.active_workflow_types,
        (channel || 'widget') as TranslationChannel,
      );
      const baseIsAutoflow =
        channel === 'api'
          ? intent.api_workflow_is_autoflow
          : channel === 'email'
            ? intent.email_autopilot_enabled
            : intent.is_autoflow;
      return {
        intentId: intent.intent_definition_id,
        intentName: intent.intent_name,
        intentWorkflowId: intent.intent_workflow_id,
        isAutoflow: isEffectivelyAutoflow(
          intent.intent_definition_id,
          baseIsAutoflow,
          isClassicWorkflowsDeprecated,
        ),
        isChecked: isActive,
        isDraft: !isActive,
      };
    });

    // sort alphabetically
    return intentFilters.sort((filterA, filterB) =>
      filterA.intentName.localeCompare(filterB.intentName),
    );
  }, [
    intentsResponse,
    handoffConfigurationsResponse,
    channel,
    isClassicWorkflowsDeprecated,
  ]);

  const intentFiltersMapping = useMemo(() => {
    const result: Record<string, IntentFilter> = {};
    intentFilters.forEach(
      intentFilter => (result[intentFilter.intentId] = intentFilter),
    );
    return result;
  }, [intentFilters]);

  return {
    defaultIntentFilters: intentFilters,
    defaultIntentFiltersMapping: intentFiltersMapping,
    isLoading: isGetIntentsLoading || isHandoffConfigurationLoading,
  };
};

export const useGetDefaultSubflowFilters = (): {
  defaultSubflowFilters: IntentFilter[];
  defaultSubflowFiltersMapping: Record<string, IntentFilter>;
  isLoading: boolean;
} => {
  const channel = useChannel();
  const productOverride =
    channel && channel !== 'email'
      ? CHANNEL_TO_PRODUCT_MAP[channel as ConversationChannel]
      : '';
  const { data: intentsResponse, isLoading: isGetIntentsLoading } =
    useGetIntentsQueryWithProduct(undefined, {
      productOverride: productOverride || 'workflow_builder',
    });
  const {
    data: handoffConfigurationsResponse,
    isLoading: isHandoffConfigurationLoading,
  } = useGetHandoffConfigurationsQuery() ?? [];
  const isClassicWorkflowsDeprecated = useIsFeatureFlagEnabled(
    'classic_workflows_deprecated',
  );

  const subflowFilters = useMemo(() => {
    const handoffConfigurations =
      handoffConfigurationsResponse?.configurations ?? [];

    // Get ONLY handoff intents (inverse of useGetDefaultIntentFilters)
    const handoffIntentIds = new Set(
      handoffConfigurations.map(h => h.intent_definition_id),
    );

    const filteredIntents =
      intentsResponse?.intents.filter(intent =>
        handoffIntentIds.has(intent.intent_definition_id),
      ) ?? [];

    const subflowFilters = filteredIntents.map(intent => {
      const isActive = isIntentActiveForChannel(
        intent.active_workflow_types,
        (channel || 'widget') as TranslationChannel,
      );
      const baseIsAutoflow =
        channel === 'api'
          ? intent.api_workflow_is_autoflow
          : channel === 'email'
            ? intent.email_autopilot_enabled
            : intent.is_autoflow;
      return {
        intentId: intent.intent_definition_id,
        intentName: intent.intent_name,
        intentWorkflowId: intent.intent_workflow_id,
        isAutoflow: isEffectivelyAutoflow(
          intent.intent_definition_id,
          baseIsAutoflow,
          isClassicWorkflowsDeprecated,
        ),
        isChecked: isActive,
        isDraft: !isActive,
      };
    });

    // sort alphabetically
    return subflowFilters.sort((filterA, filterB) =>
      filterA.intentName.localeCompare(filterB.intentName),
    );
  }, [
    intentsResponse,
    handoffConfigurationsResponse,
    channel,
    isClassicWorkflowsDeprecated,
  ]);

  const subflowFiltersMapping = useMemo(() => {
    const result: Record<string, IntentFilter> = {};
    subflowFilters.forEach(
      subflowFilter => (result[subflowFilter.intentId] = subflowFilter),
    );
    return result;
  }, [subflowFilters]);

  return {
    defaultSubflowFilters: subflowFilters,
    defaultSubflowFiltersMapping: subflowFiltersMapping,
    isLoading: isGetIntentsLoading || isHandoffConfigurationLoading,
  };
};

export const convertIntentFiltersToDict = (
  intentFilters: IntentFilter[],
): IntentFiltersForIFrame => {
  const res: IntentFiltersForIFrame = {};

  intentFilters
    .filter(intentFilter => intentFilter.isChecked)
    .forEach(intentFilter => {
      // Only include selected_version and selected_version_type when both are present
      // This ensures we don't send incomplete version data to the API
      const hasValidVersionSelection =
        intentFilter.selectedVersion != null &&
        intentFilter.selectedVersionType != null;

      res[intentFilter.intentId] = {
        is_autoflow: intentFilter.isAutoflow,
        is_draft: intentFilter.isDraft,
        ...(hasValidVersionSelection && {
          selected_version: intentFilter.selectedVersion,
          selected_version_type: intentFilter.selectedVersionType,
        }),
      };
    });

  return res;
};

export const convertIntentFiltersToDictForEmail = (
  intentFilters: IntentFilter[],
): IntentFiltersForEmail => {
  const res: IntentFiltersForEmail = {};

  intentFilters
    .filter(intentFilter => intentFilter.isChecked)
    .forEach(intentFilter => {
      res[intentFilter.intentId] = {
        is_draft: intentFilter.isDraft,
      };
    });

  return res;
};

export const isGeneralFallback = (intentId: string) => {
  return (
    intentId === CommonIntentWorkflowType.GENERAL_HANDOFF ||
    intentId === CommonIntentWorkflowType.KNOWLEDGE_ARTICLE
  );
};

/**
 * Reconciles a user's stored preview filter selections with freshly fetched
 * defaults. The fields the filter modal treats as the user's selection --
 * isChecked, isAutoflow, isDraft (both set by the modal's mode/version
 * controls), previewMode, selectedVersion, selectedVersionType -- win for
 * intents that still exist; display fields (name, workflow id) refresh from
 * the defaults; intents added since the selections were stored come in with
 * defaults, and stored selections for deleted intents fall away.
 *
 * Deliberately not validated: a stored selectedVersion may point at a version
 * that no longer exists. The preview request tolerates that today, so the
 * merge keeps the selection rather than silently dropping it.
 */
export const mergeFiltersWithDefaults = ({
  defaultFilters,
  storedFilters,
}: {
  defaultFilters: IntentFilter[];
  storedFilters: IntentFilter[];
}): IntentFilter[] => {
  const storedByIntentId = new Map(
    storedFilters.map(filter => [filter.intentId, filter]),
  );

  return defaultFilters.map(defaultFilter => {
    const stored = storedByIntentId.get(defaultFilter.intentId);
    if (!stored) {
      return defaultFilter;
    }

    return {
      ...defaultFilter,
      isAutoflow: stored.isAutoflow,
      isChecked: stored.isChecked,
      isDraft: stored.isDraft,
      // `null` is a meaningful selection (version cleared) and must survive;
      // only an absent field falls back to the default's.
      previewMode: stored.previewMode ?? defaultFilter.previewMode,
      selectedVersion:
        stored.selectedVersion !== undefined
          ? stored.selectedVersion
          : defaultFilter.selectedVersion,
      selectedVersionType:
        stored.selectedVersionType !== undefined
          ? stored.selectedVersionType
          : defaultFilter.selectedVersionType,
    };
  });
};

export const filterNPartitionIntentFilters = ({
  defaultIntentFiltersMapping,
  intentFilters,
  previewFromIntentId,
  search,
}: {
  defaultIntentFiltersMapping: Record<string, IntentFilter>;
  intentFilters: IntentFilter[];
  previewFromIntentId: string | null;
  search: string;
}): [IntentFilter | null, IntentFilter[], IntentFilter[]] => {
  const filteredIntentFilters = intentFilters.filter(intentFilter =>
    intentFilter.intentName.toLowerCase().includes(search.toLowerCase()),
  );
  const currentIntentIndex = previewFromIntentId
    ? filteredIntentFilters.findIndex(
        intentFilter => intentFilter.intentId === previewFromIntentId,
      )
    : -1;
  const currentIntent =
    currentIntentIndex > -1 ? filteredIntentFilters[currentIntentIndex] : null;
  const otherIntents =
    currentIntentIndex > -1
      ? [
          ...filteredIntentFilters.slice(0, currentIntentIndex),
          ...filteredIntentFilters.slice(currentIntentIndex + 1),
        ]
      : filteredIntentFilters;
  const [liveIntents, inactiveIntents] = partition(intentFilter => {
    return defaultIntentFiltersMapping[intentFilter.intentId]?.isChecked;
  }, otherIntents);

  return [currentIntent, liveIntents, inactiveIntents];
};

export const useGetIntentFiltersMode = (): IntentFiltersMode => {
  const intentFilters = useSelector(selectPreviewIntentFilters);
  const { defaultIntentFilters, defaultIntentFiltersMapping } =
    useGetDefaultIntentFilters();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const previewFromIntentId = params.get('intentId');

  const mode: IntentFiltersMode = useMemo(() => {
    // set up the data we need for comparisons
    const previewIntent = intentFilters.find(
      intentFilter => intentFilter.intentId === previewFromIntentId,
    );
    const isPreviewIntentAnActiveIntent =
      defaultIntentFiltersMapping[previewFromIntentId || '']?.isChecked;

    const checkedActiveIntents = intentFilters.filter(
      intentFilter =>
        intentFilter.isChecked &&
        (isPreviewIntentAnActiveIntent ||
          intentFilter.intentId !== previewFromIntentId),
    );
    const activeIntents = defaultIntentFilters.filter(
      intentFilter => intentFilter.isChecked,
    );

    // num of checked active intents don't match
    if (checkedActiveIntents.length !== activeIntents.length) {
      return 'customized';
    }

    // check whether all checked active intent filters are the same as the default intent filter
    // currently previewing active intent is excluded
    const isLiveTraffic = checkedActiveIntents.every(intentFilter => {
      if (
        intentFilter.intentId === previewFromIntentId &&
        isPreviewIntentAnActiveIntent
      ) {
        return true;
      }

      return isEqual(
        intentFilter,
        defaultIntentFiltersMapping[intentFilter.intentId],
      );
    });

    if (!isLiveTraffic) {
      return 'customized';
    }

    return previewIntent?.isChecked && previewIntent?.isDraft
      ? 'current-intent-and-live-traffic'
      : 'live-traffic';
  }, [
    intentFilters,
    defaultIntentFilters,
    defaultIntentFiltersMapping,
    previewFromIntentId,
  ]);

  return mode;
};

/**
 * The Solve preview always renders dark. It used to follow the workflow mode --
 * classic previews were light, autoflow previews dark -- but classic is
 * deprecated, so there is nothing left to switch between.
 */
export const useGetThemeBasedOnMode = () => {
  const theme = useTheme();

  const darkTheme = createTheme(theme, {
    palette: {
      colors: {
        blue: {
          500: darkModeBlue,
        },
        green: {
          500: darkModeGreen,
        },
        grey: {
          500: darkModeGrey,
        },
        purple: {
          500: darkModePurple,
        },
      },
      mode: 'dark',
    },
  });

  return darkTheme;
};

export const useGetLightTheme = () => {
  const theme = useTheme();

  const lightTheme = createTheme(theme, {
    palette: {
      mode: 'light',
    },
  });

  return lightTheme;
};

export const getTemplateIntentWorkflowId = (intentId: string) => {
  const intentWorkflowId = intentId.substring(
    intentId.indexOf(TEMPLATE_INTENT_WORKFLOW_ID_PREFIX) +
      TEMPLATE_INTENT_WORKFLOW_ID_PREFIX.length,
  );

  return intentWorkflowId;
};

export const useChannel = (): string | undefined => {
  const { channel } = useParams<'channel'>();
  const [searchParams] = useSearchParams();
  const paramChannel = searchParams.get('channel');
  const location = useLocation();

  // Handle assist preview route
  if (location.pathname.includes('/assist/preview')) {
    return 'playbook';
  }

  return channel || paramChannel || undefined;
};

export const useIsCurrentChannelEmail = () => {
  const channel = useChannel();
  return channel === 'email';
};
